import { useState } from "react";
import { Check, Plus, Save, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useGenerateInsightFields } from "@/hooks/agent/useInsights";
import { Insight, InsightGenieContext } from "@/services/api/InsightService";
import { LeadInfoField } from "@/types/leads/lead-info";

interface InsightFormProps {
  insight?: Insight;
  genieContext?: InsightGenieContext;
  onSubmit: (data: { name: string; description?: string; fields?: LeadInfoField[]; is_default?: boolean }) => void;
  onCancel: () => void;
}

export function InsightForm({ insight, genieContext, onSubmit, onCancel }: InsightFormProps) {
  const [name, setName] = useState(insight?.name || "");
  const [description, setDescription] = useState(insight?.description || "");
  const [fields, setFields] = useState<LeadInfoField[]>(insight?.fields || []);
  const [manualKeys, setManualKeys] = useState<Set<number>>(new Set(insight?.fields?.map((_, i) => i) || []));
  const [isDefault, setIsDefault] = useState(insight?.is_default || false);
  const [showAdvanced, setShowAdvanced] = useState(!!insight);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const generateInsightMutation = useGenerateInsightFields();

  const addField = () => {
    setFields((prev) => [...prev, { key: "", label: "", type: "text", required: false }]);
  };

  const updateField = (index: number, updates: Partial<LeadInfoField>) => {
    setFields((prev) => prev.map((field, i) => (i === index ? { ...field, ...updates } : field)));
  };

  const removeField = (index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
    setManualKeys((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const handleGenerateInsight = async () => {
    if (!description.trim()) return;

    try {
      const result = await generateInsightMutation.mutateAsync({ description, genieContext });
      if (result) {
        setName(result.name);
        setFields(result.fields);
        setShowAdvanced(true);
      }
    } catch (error) {
      console.error("Failed to generate insight:", error);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      onSubmit({
        name,
        description,
        fields: fields.filter((f) => f.key && f.label),
        is_default: isDefault,
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const longestKeyLength = Math.max(8, ...fields.map((field) => field.key.length));
  const keyColumnWidth = `${Math.min(longestKeyLength + 1, 32)}ch`;

  return (
    <div className="space-y-4">
      {!showAdvanced && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Insight Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Qualify leads for premium consulting services by capturing their budget, timeline, and specific needs"
              rows={5}
              className="min-h-[120px]"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Describe what you want to achieve with this insight. AI will generate the appropriate fields.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleGenerateInsight}
              disabled={!description.trim() || generateInsightMutation.isPending}
              className="flex-1 order-1"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {generateInsightMutation.isPending ? "Generating..." : "Generate Insight Fields"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdvanced(true)}
              className="order-2 sm:order-2"
            >
              Manual Setup
            </Button>
          </div>
        </div>
      )}

      {showAdvanced && (
        <>
          <div>
            <label className="block text-sm font-medium mb-2">Insight Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Premium Consulting Lead Qualification"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Insight Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this insight"
              rows={3}
              required
            />
          </div>
        </>
      )}

      {showAdvanced && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium">Fields</label>
            <div className="flex gap-2">
              {!insight && (
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAdvanced(false)}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Use AI
                </Button>
              )}
              <Button type="button" size="sm" variant="outline" onClick={addField}>
                <Plus className="h-4 w-4 mr-2" />
                Add Field
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            {fields.map((field, index) => (
              <div
                key={index}
                className="flex flex-wrap sm:flex-nowrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5"
              >
                <input
                  type="text"
                  value={field.key}
                  onChange={(e) => {
                    setManualKeys((prev) => new Set(prev).add(index));
                    updateField(index, { key: e.target.value });
                  }}
                  placeholder="key"
                  autoComplete="off"
                  style={{ width: keyColumnWidth }}
                  className="shrink-0 bg-transparent text-xs font-medium text-primary placeholder:text-gray-300 outline-none"
                />
                <div className="w-px h-3 bg-gray-200 shrink-0" />
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    const updates: Partial<LeadInfoField> = { label };
                    if (!manualKeys.has(index)) {
                      updates.key = label
                        .toLowerCase()
                        .trim()
                        .split(/\s+/)
                        .slice(0, 3)
                        .join("_")
                        .replace(/[^a-z0-9_]+/g, "");
                    }
                    updateField(index, updates);
                  }}
                  placeholder="label"
                  autoComplete="off"
                  className="flex-1 min-w-[8rem] sm:min-w-0 bg-transparent text-xs text-gray-600 placeholder:text-gray-300 outline-none"
                />
                <Select
                  value={field.type || "text"}
                  onValueChange={(value) => updateField(index, { type: value as LeadInfoField["type"] })}
                >
                  <SelectTrigger className="h-6 w-24 shrink-0 text-xs border-none bg-transparent shadow-none px-1 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="textarea">Textarea</SelectItem>
                    <SelectItem value="select">Select</SelectItem>
                  </SelectContent>
                </Select>
                <label className="relative flex items-center gap-1.5 text-xs shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.required || false}
                    onChange={(e) => updateField(index, { required: e.target.checked })}
                    className="peer sr-only"
                  />
                  <span
                    className={`flex items-center justify-center w-4 h-4 rounded peer-focus-visible:ring-2 peer-focus-visible:ring-genie-primary ${
                      field.required
                        ? "bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
                        : "bg-white border border-gray-300"
                    }`}
                  >
                    {field.required && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                  <span
                    className={
                      field.required
                        ? "font-semibold bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent"
                        : "text-gray-500"
                    }
                  >
                    Required
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => removeField(index)}
                  className="text-gray-400 hover:text-gray-600 shrink-0 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-4">
        {showAdvanced ? (
          <label className="flex items-center text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="mr-2"
            />
            Set as default insight
          </label>
        ) : (
          <div />
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="order-2 sm:order-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="order-1 sm:order-2 transition-all duration-300 bg-primary text-white hover:bg-primary/90"
            disabled={!showAdvanced || isSubmitting}
          >
            {showSuccess ? (
              <>
                <Check className="h-4 w-4 mr-2 animate-bounce" />
                Insight Saved!
              </>
            ) : isSubmitting ? (
              <>
                <Save className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Insight
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
